import * as storage from '../utils/storage';
import Settings from '../domain/Settings';
import {getGigyaId} from '../utils/gigya';
import {getCxenseProperties} from './cxenseService';
import {hasPurposesConsent, hasUserProfilingConsent, onConsentReady} from './privacyService';
import * as performance from '../utils/performance';
import MD5 from 'crypto-js/md5';

const PROFILE_SERVICE_URL = 'https://profile-public-api.data.dpgmedia.cloud/dmoi',
	DMOI_STORAGE_KEY = 'advert-dmoi',
	DMOI_FETCH_TIMEOUT = 1000,
	DEFAULT_INVALIDATION_HOUR = 5,
	DEFAULT_CACHE_DAYS = 1,
	ROTATING_FREQUENCY_IN_DAYS = 7;

let userId: string,
	rotatingUserId: string;

export async function init(): Promise<void> {
	if (!Settings.getInstance().features.dmoi) {
		userId = null;

		return;
	}

	const userIds = _getFromStorage();

	if (userIds) {
		userId = userIds.userId;
		rotatingUserId = userIds.rotatingUserId;

		if (!rotatingUserId) {
			rotatingUserId = _calculateRotatingUserId(userId);

			_cacheUserIds();
		}
	} else {
		userId = await _fetchDMOI();
		rotatingUserId = userId ? _calculateRotatingUserId(userId) : null;

		_cacheUserIds();
	}
}

function _getIdentifiers() {
	return Object.fromEntries(Object.entries({
		'pw_id': storage.getFromCookie('authId'),
		'gigya_id': getGigyaId(),
		'cxense_id': getCxenseProperties()?.id
	}).filter(([, v]) => v));
}

async function _fetchDMOI(): Promise<string | null> {
	try {
		performance.mark('user - dmoi - start');

		const identifiers = _getIdentifiers();

		if (Object.keys(identifiers).length === 0) {
			return null;
		}

		const {
				apiKey,
				url = PROFILE_SERVICE_URL
			} = Settings.getInstance().dmoi,
			abortController = new AbortController(),
			abortTimeout = setTimeout(() => {
				performance.mark('user - dmoi - timeout');
				abortController.abort('Timed out');
			}, DMOI_FETCH_TIMEOUT),
			response = await window.fetch(url, {
				'method': 'POST',
				'headers': {
					'x-api-key': apiKey
				},
				'body': JSON.stringify(identifiers),
				'signal': abortController.signal
			});

		clearTimeout(abortTimeout);

		return (await response.json()).dmoi;
	} catch (error) {
		console.error('[ADVERT] Could not fetch DMOI', error);

		return null;
	} finally {
		performance.mark('user - dmoi - end');
	}
}

function _cacheUserIds() {
	onConsentReady(() => {
		if (hasPurposesConsent([1])) {
			storage.put(DMOI_STORAGE_KEY, JSON.stringify({
				'value': userId,
				'expiration': _getExpirationDate(),
				'rdmoi': rotatingUserId
			}));
		}
	});
}

function _getExpirationDate(): string {
	const currDate = new Date();

	currDate.setDate(currDate.getDate() + (Settings.getInstance().dmoi?.cache?.duration ?? DEFAULT_CACHE_DAYS));
	currDate.setUTCHours(Settings.getInstance().dmoi?.cache?.invalidationHour ?? DEFAULT_INVALIDATION_HOUR, 0, 0, 0);

	return currDate.toISOString();
}

function _getFromStorage(): Record<string, string> | null {
	const storageValue = storage.get(DMOI_STORAGE_KEY);

	if (!storageValue) {
		return null;
	}

	const {
		expiration,
		value,
		rdmoi
	} = JSON.parse(storageValue);

	if (new Date(expiration) < new Date()) {
		return null;
	}

	return value === null ? null : {
		userId: value,
		rotatingUserId: rdmoi
	};
}

function _calculateRotatingUserId(dmoi: string) {
	const date = new Date(),
		dayOfYear = _getDayOfYear(date),
		year = date.getFullYear().toString(),
		rotation = Math.floor(dayOfYear / ROTATING_FREQUENCY_IN_DAYS).toString();

	return MD5(dmoi + year + rotation + '53nN3cr0u65').toString();
}

function _getDayOfYear(date: Date) {
	const currentDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
		firstDayOfYear = Date.UTC(date.getFullYear(), 0, 0);

	return (currentDate - firstDayOfYear) / 24 / 60 / 60 / 1000;
}

export function getUserId(): string {
	if (!hasUserProfilingConsent()) {
		return null;
	}

	return userId;
}

export function isUserLoggedIn(): boolean {
	return !!getGigyaId();
}

export function getRandomizedUserId(): string {
	if (!hasUserProfilingConsent()) {
		return null;
	}

	return rotatingUserId;
}
